import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { date, DEMO_TODAY, daysBetween, money } from '../../lib/format';
import { useInvestorScope } from '../../store/selectors';
import { KeyValueGrid } from '../../ui/primitives';
import { Card, SectionLabel } from '../common';

export default function OfferPage() {
  const { offerId } = useParams();
  const scope = useInvestorScope();
  const navigate = useNavigate();
  const offer = scope.offers.find((item) => item.id === offerId);
  if (!offer) return <Alert severity="info">This offer is not open.</Alert>;
  const fund = scope.funds.find((item) => item.id === offer.fundId);
  const unitClass = scope.classes.find((item) => item.id === offer.classId);
  const daysLeft = daysBetween(DEMO_TODAY, offer.closesAt);
  const [closeDate, closeTime] = offer.closesAt.split('T');
  return (
    <>
      <Box
        sx={{
          bgcolor: offer.brandColour,
          color: '#fff',
          borderRadius: '10px',
          p: { xs: 2.5, md: 3.5 },
          mb: 2.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{ opacity: 0.8, letterSpacing: '0.2em', fontWeight: 700 }}
        >
          FIDES · {offer.url}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ mt: 1 }}>
          {fund?.name}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 1, maxWidth: 640 }}>
          {offer.summary}
        </Typography>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Button
            variant="contained"
            onClick={() => navigate(`/investor/apply/new?fund=${offer.fundId}`)}
            sx={{ bgcolor: '#fff', color: offer.brandColour, '&:hover': { bgcolor: '#f1f5f9' } }}
          >
            Apply now
          </Button>
          {offer.showCountdown && daysLeft >= 0 && (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {daysLeft} days left to apply
            </Typography>
          )}
        </Stack>
      </Box>
      <SectionLabel>Key terms</SectionLabel>
      <Card sx={{ mb: 2.5 }}>
        <KeyValueGrid
          columns={3}
          items={[
            { label: 'Unit class', value: unitClass?.name },
            {
              label: 'Target return',
              value: unitClass ? `${unitClass.targetReturnPct}% p.a.` : undefined,
            },
            {
              label: 'Distributions',
              value: fund?.distributionFrequency.toLowerCase().replace('_', '-'),
            },
            {
              label: 'Payable now',
              value: unitClass?.paidOnApplicationPct
                ? `$${((unitClass.issuePrice * unitClass.paidOnApplicationPct) / 100).toFixed(2)} per unit, balance called later`
                : `$${(unitClass?.issuePrice ?? 1).toFixed(2)} per unit (fully paid)`,
            },
            { label: 'Minimum', value: money(offer.minInvestment) },
            { label: 'Increments of', value: money(offer.increment) },
            { label: 'Maximum', value: offer.maxInvestment ? money(offer.maxInvestment) : 'None' },
            { label: 'Applications close', value: `${date(closeDate)}, ${closeTime}` },
            { label: 'Pay by', value: offer.paymentMethods.join(' or ') },
            { label: 'Trustee', value: fund?.trustee },
          ]}
        />
      </Card>
      <SectionLabel>Offer documents</SectionLabel>
      <Card>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {offer.documents.map((document) => (
            <Chip
              key={document}
              icon={<DescriptionOutlinedIcon />}
              label={document}
              variant="outlined"
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          Wholesale investors only. Read the Information Memorandum before applying.
        </Typography>
      </Card>
    </>
  );
}
